import React from "react";
import { ArrowRight, Compass, TrendingUp, Users } from "lucide-react";
import { Section, Kicker, Heading, Button } from "../components/ui";
import Reveal from "../components/Reveal";

const REASONS = [
  { icon: Compass, title: "A clear purpose", text: "We know what we are building and why. You will always understand how your work contributes to the group." },
  { icon: TrendingUp, title: "Room to grow", text: "As the group grows, so does the scope to take on responsibility and develop your career." },
  { icon: Users, title: "A close team", text: "You will work directly with experienced colleagues in a supportive, low-ego environment." },
];

const ROLES = [
  { title: "Acquisitions Analyst", location: "London", type: "Full-time" },
  { title: "Property Manager", location: "Remote (UK)", type: "Full-time" },
  { title: "Finance Assistant", location: "London", type: "Part-time" },
];

const Careers = () => {
  return (
    <>
      <section className="bg-navy text-white pt-36 pb-24 lg:pt-44 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Kicker light>Careers</Kicker>
            <Heading as="h1" className="text-4xl md:text-5xl lg:text-6xl max-w-4xl">
              Join a serious, long-term property group.
            </Heading>
            <p className="mt-8 text-lg text-steel-light max-w-2xl leading-relaxed">
              We are a small and growing team. If you value discipline, autonomy and the chance to
              help shape a business, we would like to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Why OTP Group */}
      <Section>
        <Reveal>
          <div className="max-w-2xl">
            <Kicker>Why OTP Group</Kicker>
            <Heading className="text-3xl md:text-4xl">A serious place to build a career.</Heading>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-navy/10">
          {REASONS.map((r, i) => {
            const Icon = r.icon;
            return (
              <Reveal key={i} delay={i * 0.06}>
                <div className="bg-white p-10 h-full">
                  <Icon size={28} strokeWidth={1.5} className="text-steel" />
                  <h3 className="mt-6 text-xl font-semibold tracking-heading">{r.title}</h3>
                  <p className="mt-3 text-steel leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* Open roles */}
      <Section stone>
        <Reveal>
          <div className="max-w-2xl">
            <Kicker>Open roles</Kicker>
            <Heading className="text-3xl md:text-4xl">Current opportunities.</Heading>
          </div>
        </Reveal>
        <div className="mt-12 divide-y divide-navy/10 border-y border-navy/10">
          {ROLES.map((role, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-7 group"
                data-testid={`role-${i}`}
              >
                <div>
                  <h3 className="text-xl font-semibold tracking-heading">{role.title}</h3>
                  <div className="mt-1 flex gap-4 text-sm text-steel">
                    <span>{role.location}</span>
                    <span>·</span>
                    <span>{role.type}</span>
                  </div>
                </div>
                <Button to="/contact?path=careers" variant="outline" testid={`role-apply-${i}`}>
                  Register interest <ArrowRight size={16} />
                </Button>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-navy text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <Heading className="text-3xl md:text-4xl">Don't see the right role?</Heading>
              <p className="mt-4 text-steel-light leading-relaxed">
                We are always interested to hear from talented people, even when we are not actively
                recruiting. Send us your details and tell us how you could contribute.
              </p>
            </div>
            <Button to="/contact?path=careers" variant="primaryLight" testid="careers-cta" className="shrink-0">
              Register your interest <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Careers;
